import {
  serviceGetLivechatAttachmentStatus,
  servicePostLivechatUpload,
  servicePostLivechatUploadComplete,
} from "../../../services/livechat";
import { newClientId } from "./livechatState";

const MB = 1024 * 1024;
// stored images and PDFs: at most 2 MiB, whatever the admin setting
const MAX_STORED_BYTES = 2 * MB;
const MAX_THUMBNAIL_BYTES = 204800;
const PART_BYTES = 8 * MB;
const PUT_TIMEOUT_MS = 120000;
const POLL_INTERVAL_MS = 2000;
const POLL_ATTEMPTS = 60;
const VIDEO_METADATA_TIMEOUT_MS = 5000;

// Live Chat's admin settings when the site's limits can't be read; the server checks again
export const FALLBACK_LIMITS = {
  max_upload_bytes: 20 * MB,
  upload_types: ["application/pdf", "image/gif", "image/jpeg", "image/png", "image/webp"],
  max_video_bytes: 100 * MB,
  max_video_seconds: 600,
  video_types: ["video/mp4", "video/quicktime"],
};

// One picker per kind, as on the WhatsApp chat; empty when the admin switched it off.
// HEIC is converted in the browser when images are allowed.
export const acceptOf = (limits) => {
  const images = limits.upload_types.filter((type) => type.startsWith("image/"));

  return {
    image: (images.length ? [...images, "image/heic", "image/heif"] : []).join(","),
    file: limits.upload_types.filter((type) => type === "application/pdf").join(","),
    video: limits.video_types.join(","),
  };
};

export class LivechatUploadError extends Error {}

const mediaMessages = {
  FILE_TOO_LARGE: (limits, isVideo) =>
    `File terlalu besar, maks. ${Math.round((isVideo ? limits.max_video_bytes : limits.max_upload_bytes) / MB)} MB`,
  IMAGE_TOO_MANY_PIXELS: () => "Resolusi gambar terlalu besar",
  IMAGE_UNREADABLE: () => "Gambar tidak bisa dibaca",
  PDF_TOO_LARGE: () => "PDF tidak bisa dikompres di bawah 2 MB",
  PDF_LOCKED: () => "PDF dikunci password",
  PDF_UNREADABLE: () => "PDF tidak bisa dibaca",
  VIDEO_UNSUPPORTED: () => "Browser ini tidak bisa mengonversi video, coba Chrome atau Edge terbaru",
  VIDEO_UNREADABLE: () => "Video tidak bisa dibaca",
  VIDEO_TOO_LONG: (limits) => `Video lebih dari ${limits.max_video_seconds} detik`,
  VIDEO_NOT_SMALLER: () => "Video tidak bisa dikompres",
};

const rejectMessages = {
  FILE_TOO_LARGE: "File terlalu besar",
  RESOLUTION_EXCEEDED: "Resolusi terlalu besar",
  CODEC_NOT_ALLOWED: "Format video tidak didukung",
  DURATION_EXCEEDED: "Video terlalu panjang",
  BAD_FORMAT: "Format file tidak valid",
  MALWARE: "File terdeteksi berbahaya",
};

export const rejectMessage = (reason) => rejectMessages[reason] ?? "File ditolak";

const serverMessages = {
  "error.livechat.rate_limited": "Terlalu banyak upload, coba lagi sebentar",
  "error.livechat.not_holder": "Hanya pemegang chat yang bisa mengirim file",
  "error.livechat.scan_unavailable": "Pemeriksaan file sedang tidak tersedia, coba lagi 1-2 menit",
  "error.livechat.upload_out_of_spec": "File tidak sesuai ketentuan Live Chat",
};

const serverError = (res) =>
  new LivechatUploadError(serverMessages[res?.data?.message] ?? "Upload gagal, coba lagi");

export const kindOf = (file) => {
  if (file.type.startsWith("video/")) return "video";

  return file.type === "application/pdf" ? "file" : "image";
};

const videoSeconds = (file) =>
  new Promise((resolve) => {
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    const done = (seconds) => {
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      video.removeAttribute("src");
      resolve(seconds);
    };
    const timer = setTimeout(() => done(null), VIDEO_METADATA_TIMEOUT_MS);
    video.preload = "metadata";
    video.onloadedmetadata = () => done(Number.isFinite(video.duration) ? video.duration : null);
    video.onerror = () => done(null);
    video.src = url;
  });

const MP4_CONTAINERS = new Set(["moov", "trak", "mdia", "minf", "stbl"]);

// true when the MP4 has a sound handler; the SDK drops the audio track of a silent video
export const mp4HasAudio = async (file) => {
  const read = async (at, length) => new Uint8Array(await file.slice(at, Math.min(file.size, at + length)).arrayBuffer());
  const u32 = (bytes, offset) => new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(offset);
  const ascii = (bytes, offset) => String.fromCharCode(...bytes.slice(offset, offset + 4));

  const walk = async (start, end) => {
    for (let at = start; at + 8 <= end; ) {
      const head = await read(at, 16);
      if (head.length < 8) return false;

      let size = u32(head, 0);
      let header = 8;
      const type = ascii(head, 4);

      if (size === 1) {
        if (head.length < 16) return false;
        size = u32(head, 8) * 2 ** 32 + u32(head, 12);
        header = 16;
      } else if (size === 0) {
        size = end - at;
      }

      if (size < header || at + size > end) return false;

      if (type === "hdlr") {
        const body = await read(at + header, 12);
        if (body.length >= 12 && ascii(body, 8) === "soun") return true;
      } else if (MP4_CONTAINERS.has(type) && (await walk(at + header, at + size))) {
        return true;
      }

      at += size;
    }

    return false;
  };

  try {
    return await walk(0, file.size);
  } catch {
    return false;
  }
};

const webpThumbnail = (blob) =>
  blob?.type === "image/webp" && blob.size >= 1 && blob.size <= MAX_THUMBNAIL_BYTES ? blob : undefined;

const imageSize = async (compressed) => {
  if (compressed.width && compressed.height) {
    return { width: Math.round(compressed.width), height: Math.round(compressed.height) };
  }

  const bitmap = await createImageBitmap(compressed.file);
  const size = { width: bitmap.width, height: bitmap.height };
  bitmap.close();

  return size;
};

const prepareVideo = async (media, file, limits, onProgress) => {
  const compressed = await media.compressVideo(file, {
    maxBytes: limits.max_video_bytes,
    maxSeconds: limits.max_video_seconds,
    onProgress: (percent) => onProgress("compress", percent),
  });
  const seconds = await videoSeconds(compressed.file);

  if (!seconds) throw new LivechatUploadError("Video tidak bisa dibaca");

  // the preview is JPEG: only a WebP thumbnail may be uploaded
  return {
    file: compressed.file,
    declaration: {
      kind: "video",
      contentType: "video/mp4",
      filename: compressed.file.name || "video.mp4",
      size: compressed.file.size,
      width: Math.round(compressed.width),
      height: Math.round(compressed.height),
      videoCodec: "h264",
      audioCodec: (await mp4HasAudio(compressed.file)) ? "aac" : "",
      durationMs: Math.max(1, Math.round(seconds * 1000)),
    },
  };
};

const prepareImageOrPdf = async (media, file, limits, onProgress) => {
  const compressed = await media.compressMedia(file, {
    maxBytes: limits.max_upload_bytes,
    onProgress: (percent) => onProgress("compress", percent),
  });
  const { file: out } = compressed;

  if (out.size < 1 || out.size > MAX_STORED_BYTES) {
    throw new LivechatUploadError("File tidak bisa dikompres di bawah 2 MB");
  }

  const thumbnail = webpThumbnail(compressed.thumbnail);
  const common = { filename: out.name, size: out.size, thumbnailSize: thumbnail?.size };

  if (out.type === "application/pdf") {
    return { file: out, thumbnail, declaration: { ...common, kind: "file", contentType: out.type } };
  }

  return {
    file: out,
    thumbnail,
    declaration: { ...common, kind: "image", contentType: out.type, ...(await imageSize(compressed)) },
  };
};

// Compresses in the browser with the Live Chat SDK; the server still validates the bytes
export const prepareLivechatUpload = async (file, limits, onProgress) => {
  const media = await import("@livechat/media");
  const isVideo = kindOf(file) === "video";

  try {
    const prepared = isVideo
      ? await prepareVideo(media, file, limits, onProgress)
      : await prepareImageOrPdf(media, file, limits, onProgress);

    return { ...prepared, declaration: { ...prepared.declaration, sdkVersion: media.version } };
  } catch (error) {
    if (error instanceof LivechatUploadError) throw error;

    const message = error instanceof media.MediaError ? mediaMessages[error.code]?.(limits, isVideo) : undefined;
    throw new LivechatUploadError(message ?? "File tidak bisa dibaca");
  }
};

// No Authorization header: the presigned url is the whole credential
const putBytes = (url, body, headers, onLoaded) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const fail = () => reject(new LivechatUploadError("Upload gagal, coba lagi"));

    xhr.open("PUT", url);
    Object.entries(headers ?? {}).forEach(([key, value]) => xhr.setRequestHeader(key, value));
    xhr.timeout = PUT_TIMEOUT_MS;
    xhr.upload.onprogress = (e) => e.lengthComputable && onLoaded(e.loaded);
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : fail());
    xhr.onerror = fail;
    xhr.ontimeout = fail;
    xhr.send(body);
  });

// declare -> PUT the bytes straight to Live Chat storage -> complete
export const uploadLivechatMedia = async (token, conversationId, prepared, onProgress) => {
  const { file, thumbnail, declaration } = prepared;
  const created = await servicePostLivechatUpload(token, conversationId, { clientId: newClientId(), ...declaration });

  if (created?.status !== 200 && created?.status !== 201) throw serverError(created);

  const intent = created.data;
  const total = file.size + (thumbnail?.size ?? 0);
  let done = 0;
  const put = async (url, body, headers) => {
    await putBytes(url, body, headers, (loaded) => onProgress("upload", Math.round(((done + loaded) / total) * 100)));
    done += body.size;
  };

  if (intent.parts?.length) {
    const parts = [...intent.parts].sort((a, b) => a.number - b.number);
    for (const part of parts) {
      const start = (part.number - 1) * PART_BYTES;
      await put(part.url, file.slice(start, start + part.size));
    }
  } else {
    await put(intent.put.url, file, intent.put.headers);
  }

  if (thumbnail && intent.thumbnail) await put(intent.thumbnail.url, thumbnail, intent.thumbnail.headers);

  const completed = await servicePostLivechatUploadComplete(token, conversationId, intent.id);

  if (completed?.status !== 200 && completed?.status !== 201) throw serverError(completed);
  if (completed.data.status === "rejected") throw new LivechatUploadError("File ditolak Live Chat");

  return { attachmentId: completed.data.attachment_id, kind: declaration.kind };
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Waits for validating -> ready | rejected; null when it gave up or was cancelled
export const pollLivechatAttachment = async (token, conversationId, attachmentId, isCancelled) => {
  for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
    await sleep(POLL_INTERVAL_MS);
    if (isCancelled()) return null;

    const res = await serviceGetLivechatAttachmentStatus(token, conversationId, attachmentId);

    // only the uploader may ask: anyone else waits for the realtime update
    if (res?.status !== 200) return null;
    if (res.data.status !== "validating") return res.data;
  }

  return null;
};
